import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { concat, defer, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import produce from 'immer';
import {
  mapPatient,
  Patient,
  TestPatient,
  RegistrationTestPatient,
  buildUpdateRegistrationTestPatientContactsRequest,
  PaymentMethod,
  mapPaymentMethod,
  testPatientsTestData,
  TestPatientPaymentMethodOption,
  ApiCreateCreditCardPaymentMethodRequest,
  TestPatientNotificationOption,
  ApiCreateTestPatientNotificationRequest,
  TestPatientCopayExclusionOption,
  TestAgentOtp,
  TestPatientDeductibleOverrideOption,
} from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import {
  ApiStoreData,
  CcsSentryError,
  defaultApiStoreData,
  defaultApiStoreIterableData,
  MissingApiRequestDataError,
} from '../utils';
import { HttpErrorResponse } from '@angular/common/http';
import {
  UpdateRegistrationTestPatient,
  LoadTestPatient,
  FetchTestPatients,
  FetchRegistrationTestPatients,
  UpdateTestPatient,
  CreateTestAgentOtp,
  ResetTestPatient,
} from './test-patients.actions';
import { SignInTestPatient } from '../user/user.actions';

export interface TestPatientsStateModel {
  testPatient: {
    id: number | null;
    isLoading: boolean;
  };
  testPatients: ApiStoreData<TestPatient[]>;
  updateTestPatients: (ApiStoreData<Patient | null> & { patientId: number })[];
  registrationTestPatients: ApiStoreData<RegistrationTestPatient[]>;
  updateRegistrationTestPatients: ApiStoreData<{ email: string; patientId: number }>[];
  createTestAgentOtp: ApiStoreData<TestAgentOtp | null>;
  clearTestPatientNotifications: ApiStoreData<null>;
  createTestPatientNotification: ApiStoreData<null>;
  resetTestPatientPaymentMethods: ApiStoreData<null>;
  createTestPatientCreditCard: ApiStoreData<PaymentMethod[]>;
  createTestPatientBankAccount: ApiStoreData<PaymentMethod | null>;
  setTestPatientCopayExclusion: ApiStoreData<null>;
  expireTestPatientCreditCard: ApiStoreData<null>;
  setTestPatientDeductibleOverrides: ApiStoreData<null>;
}

export const testPatientDefaults = {
  testPatient: {
    id: null,
    isLoading: false,
  },
  testPatients: defaultApiStoreIterableData,
  updateTestPatients: [],
  registrationTestPatients: defaultApiStoreIterableData,
  updateRegistrationTestPatients: [],
  createTestAgentOtp: defaultApiStoreData,
  clearTestPatientNotifications: defaultApiStoreData,
  createTestPatientNotification: defaultApiStoreData,
  resetTestPatientPaymentMethods: defaultApiStoreData,
  createTestPatientCreditCard: defaultApiStoreIterableData,
  createTestPatientBankAccount: defaultApiStoreData,
  setTestPatientCopayExclusion: defaultApiStoreData,
  expireTestPatientCreditCard: defaultApiStoreData,
  setTestPatientDeductibleOverrides: defaultApiStoreData,
};

@State<TestPatientsStateModel>({
  name: 'testPatients',
  defaults: testPatientDefaults,
})
@Injectable({ providedIn: 'root' })
export class TestPatientsState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static testPatients(state: TestPatientsStateModel) {
    return state.testPatients.data;
  }

  @Selector()
  static isLoading(state: TestPatientsStateModel) {
    return state.testPatients.isLoading;
  }

  @Selector()
  static error(state: TestPatientsStateModel) {
    return state.testPatients.error;
  }

  @Selector()
  static testPatientId(state: TestPatientsStateModel) {
    return state.testPatient.id;
  }

  @Selector()
  static testPatientLoading(state: TestPatientsStateModel) {
    return state.testPatient.isLoading;
  }

  @Selector()
  static testPatientCreditCards(state: TestPatientsStateModel) {
    return state.createTestPatientCreditCard.data;
  }

  @Selector()
  static loadTestPatientErrors(state: TestPatientsStateModel): CcsSentryError[] | null {
    const errors = [
      state.resetTestPatientPaymentMethods.error,
      state.createTestPatientCreditCard.error,
      state.createTestPatientBankAccount.error,
      state.expireTestPatientCreditCard.error,
      state.clearTestPatientNotifications.error,
      state.createTestPatientNotification.error,
      state.setTestPatientCopayExclusion.error,
      state.setTestPatientDeductibleOverrides.error,
    ].filter((e) => !!e) as CcsSentryError[];

    return errors.length === 0 ? null : errors;
  }

  @Selector()
  static loadTestPatientHasErrors(state: TestPatientsStateModel): boolean {
    return (
      !!state.resetTestPatientPaymentMethods.error ||
      !!state.createTestPatientCreditCard.error ||
      !!state.createTestPatientBankAccount.error ||
      !!state.expireTestPatientCreditCard.error ||
      !!state.clearTestPatientNotifications.error ||
      !!state.createTestPatientNotification.error ||
      !!state.setTestPatientCopayExclusion.error ||
      !!state.setTestPatientDeductibleOverrides.error
    );
  }

  @Selector()
  static updateTestPatientsState(
    state: TestPatientsStateModel
  ): (ApiStoreData<Patient | null> & { patientId: number })[] {
    return state.updateTestPatients;
  }

  @Selector()
  static registrationTestPatientsState(state: TestPatientsStateModel) {
    const data = state.registrationTestPatients.data.map((rtp) => {
      const updateState = state.updateRegistrationTestPatients.find((s) => s.data.patientId === rtp.patientId);
      if (updateState) {
        return {
          ...rtp,
          ...updateState,
        };
      } else {
        return {
          ...defaultApiStoreData,
          ...rtp,
        };
      }
    });

    return { ...state.registrationTestPatients, data };
  }

  @Selector()
  static createTestAgentOtpState(state: TestPatientsStateModel): ApiStoreData<TestAgentOtp | null> {
    return state.createTestAgentOtp;
  }

  @Action(LoadTestPatient)
  loadTestPatient(ctx: StateContext<TestPatientsStateModel>, action: LoadTestPatient) {
    ctx.setState(
      produce((draft) => {
        draft.testPatient.id = action.patientId;
        draft.testPatient.isLoading = true;
        draft.resetTestPatientPaymentMethods = defaultApiStoreData;
        draft.createTestPatientCreditCard = defaultApiStoreIterableData;
        draft.createTestPatientBankAccount = defaultApiStoreData;
        draft.expireTestPatientCreditCard = defaultApiStoreData;
        draft.clearTestPatientNotifications = defaultApiStoreData;
        draft.createTestPatientNotification = defaultApiStoreData;
        draft.setTestPatientCopayExclusion = defaultApiStoreData;
        draft.setTestPatientDeductibleOverrides = defaultApiStoreData;
      })
    );

    return concat(
      this.handlePaymentMethod(ctx, action.paymentMethodOption, action.patientId),
      defer(this.handleNotification.bind(this, ctx, action.notificationOption, action.patientId)),
      defer(this.handleCopayExclusion.bind(this, ctx, action.copayExclusionOption, action.patientId)),
      defer(this.handleDeductibleOverrides.bind(this, ctx, action.deductibleOverrideOption, action.patientId)),
      defer(() => {
        if (!this.store.selectSnapshot(TestPatientsState.loadTestPatientHasErrors)) {
          this.store.dispatch(new SignInTestPatient(action.token));
        }
        return of(null);
      }),
      defer(this.cleanUpLoadTestPatient.bind(this, ctx))
    );
  }

  @Action(FetchTestPatients)
  fetchTestPatients(ctx: StateContext<TestPatientsStateModel>, action: FetchTestPatients) {
    ctx.setState(
      produce((draft) => {
        draft.testPatients.isLoading = true;
      })
    );
    return this.apiService.getTestPatients(action.body).pipe(
      tap({
        next: (response) => {
          this.handleSuccess({ ctx, data: response.data, stateKey: 'testPatients' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'testPatients' }))
    );
  }

  @Action(FetchRegistrationTestPatients)
  fetchRegistrationTestPatients(ctx: StateContext<TestPatientsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.registrationTestPatients.isLoading = true;
      })
    );
    return this.apiService.getRegistrationTestPatients().pipe(
      tap({
        next: (response) => {
          this.handleSuccess({ ctx, data: response.data, stateKey: 'registrationTestPatients' });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'registrationTestPatients' })
      )
    );
  }

  @Action(ResetTestPatient)
  restTestPatient(ctx: StateContext<TestPatientsStateModel>) {
    ctx.setState(
      produce((draft) => {
        Object.assign(draft, testPatientDefaults);
      })
    );
  }

  @Action(UpdateTestPatient)
  updateTestPatient(ctx: StateContext<TestPatientsStateModel>, action: UpdateTestPatient) {
    let testPatientIndex = ctx.getState().updateTestPatients.findIndex((s) => s.patientId === action.patientId);

    ctx.setState(
      produce((draft) => {
        if (testPatientIndex !== -1) {
          draft.updateTestPatients[testPatientIndex].isUpdating = true;
        } else {
          testPatientIndex =
            draft.updateTestPatients.push({
              data: null,
              isFinished: false,
              isLoading: false,
              isUpdating: true,
              error: null,
              errorUpdating: null,
              patientId: action.patientId,
            }) - 1;
        }
      })
    );

    const stateKey = `updateTestPatients[${testPatientIndex}]`;

    return this.apiService.updatePatient(action.patientId, action.body, true).pipe(
      tap({
        next: (response) => {
          const data = mapPatient(response.data[0]);
          this.handleSuccess({ ctx, data, stateKey, updating: true });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey, updating: true }))
    );
  }

  @Action(UpdateRegistrationTestPatient)
  updateRegistrationTestPatient(ctx: StateContext<TestPatientsStateModel>, action: UpdateRegistrationTestPatient) {
    let testPatientIndex = ctx
      .getState()
      .updateRegistrationTestPatients.findIndex((s) => s.data.patientId === action.patientId);

    ctx.setState(
      produce((draft) => {
        if (testPatientIndex !== -1) {
          draft.updateRegistrationTestPatients[testPatientIndex] = {
            ...draft.updateRegistrationTestPatients[testPatientIndex],
            data: {
              email: action.email,
              patientId: action.patientId,
            },
            isUpdating: true,
            error: null,
          };
        } else {
          testPatientIndex =
            draft.updateRegistrationTestPatients.push({
              data: {
                email: action.email,
                patientId: action.patientId,
              },
              isFinished: false,
              isLoading: false,
              isUpdating: true,
              error: null,
              errorUpdating: null,
            }) - 1;
        }
      })
    );

    const stateKey = `updateRegistrationTestPatients[${testPatientIndex}]`;

    return this.apiService
      .updateRegistrationTestPatientContacts(
        buildUpdateRegistrationTestPatientContactsRequest(action.patientId, action.email)
      )
      .pipe(
        tap({
          next: (response) => {
            this.handleSuccess({ ctx, stateKey, updating: true });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey, updating: true }))
      );
  }

  @Action(CreateTestAgentOtp)
  createTestAgentOtp(ctx: StateContext<TestPatientsStateModel>, action: CreateTestAgentOtp) {
    ctx.setState(
      produce((draft) => {
        draft.createTestAgentOtp.isLoading = true;
      })
    );

    return this.apiService
      .createTestAgentOtp({ patientId: action.formValues.patientId }, action.formValues.agentToken)
      .pipe(
        tap({
          next: (response) => {
            const data = response.data;
            this.handleSuccess({ ctx, stateKey: 'createTestAgentOtp', data });
          },
        }),
        catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createTestAgentOtp' }))
      );
  }

  private resetTestPatientPaymentMethods(
    ctx: StateContext<TestPatientsStateModel>,
    patientId: number
  ): Observable<any> {
    ctx.setState(
      produce((draft) => {
        draft.resetTestPatientPaymentMethods.isLoading = true;
      })
    );

    return this.apiService.resetTestPatientPaymentMethods(patientId).pipe(
      tap({
        next: () => this.handleSuccess({ ctx, stateKey: 'resetTestPatientPaymentMethods' }),
      }),
      catchError((error: any): Observable<any> => {
        console.log('Reset test patient payment methods error caught: ', error);
        if (error instanceof HttpErrorResponse) {
          console.log('Reset test patient payment methods error headers: ', error.headers);
        }
        const ignoreErrorMessage = 'http failure during parsing';
        const errorMessages = [error.message, error.error?.message].filter(
          (e) => !!e && e.toLowerCase().includes(ignoreErrorMessage)
        );
        if (errorMessages.length > 0) {
          console.log('Reset test patient payment methods error successfully ignored');
          return this.handleSuccess({ ctx, stateKey: 'resetTestPatientPaymentMethods' });
        } else {
          console.log('Reset test patient payment methods error not ignored');
          return this.handleError({ ctx, error, stateKey: 'resetTestPatientPaymentMethods', resetData: false });
        }
      })
    );
  }

  private createTestPatientCreditCard(
    ctx: StateContext<TestPatientsStateModel>,
    patientId: number,
    body: ApiCreateCreditCardPaymentMethodRequest
  ): Observable<string | null> {
    if (this.store.selectSnapshot(TestPatientsState.loadTestPatientHasErrors)) {
      return of(null);
    }

    ctx.setState(
      produce((draft) => {
        draft.createTestPatientCreditCard.isLoading = true;
      })
    );

    const currentCreditCardData: PaymentMethod[] = ctx.getState().createTestPatientCreditCard.data;

    return this.apiService.createPaymentMethod(patientId, body, true).pipe(
      tap({
        next: (creditCardResponse) => {
          this.handleSuccess({
            ctx,
            stateKey: 'createTestPatientCreditCard',
            data: [...currentCreditCardData, mapPaymentMethod(creditCardResponse.data)],
          });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createTestPatientCreditCard' })
      )
    );
  }

  private createTestPatientBankAccount(ctx: StateContext<TestPatientsStateModel>, patientId: number): Observable<any> {
    if (this.store.selectSnapshot(TestPatientsState.loadTestPatientHasErrors)) {
      return of(null);
    }

    ctx.setState(
      produce((draft) => {
        draft.createTestPatientBankAccount.isLoading = true;
      })
    );

    return this.apiService.createPaymentMethod(patientId, testPatientsTestData.createBankAccountRequest, true).pipe(
      tap({
        next: (response) => {
          this.handleSuccess({
            ctx,
            stateKey: 'createTestPatientBankAccount',
            data: mapPaymentMethod(response.data),
          });
        },
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createTestPatientBankAccount' })
      )
    );
  }

  private expireTestPatientCreditCard(ctx: StateContext<TestPatientsStateModel>, patientId: number, ccIdx: number) {
    if (this.store.selectSnapshot(TestPatientsState.loadTestPatientHasErrors)) {
      return of(null);
    }

    ctx.setState(
      produce((draft) => {
        draft.expireTestPatientCreditCard.isLoading = true;
      })
    );

    const ccId = ctx.getState().createTestPatientCreditCard.data[ccIdx]?.id;

    if (!ccId) {
      const error = new MissingApiRequestDataError(['TestPatient credit card id is missing']);

      return this.handleError({ ctx, error, stateKey: 'expireTestPatientCreditCard', resetData: false });
    }

    return this.apiService
      .expireTestPatientPaymentMethod(patientId, ccId, testPatientsTestData.expireCreditCardRequest)
      .pipe(
        tap({
          next: () => this.handleSuccess({ ctx, stateKey: 'expireTestPatientCreditCard' }),
        }),
        catchError(
          (error: Error): Observable<any> =>
            this.handleError({ ctx, error, stateKey: 'expireTestPatientCreditCard', resetData: false })
        )
      );
  }

  private handlePaymentMethod(
    ctx: StateContext<TestPatientsStateModel>,
    paymentMethodOption: TestPatientPaymentMethodOption,
    patientId: number
  ): Observable<any> {
    const { createFirstCreditCardRequest, createSecondCreditCardRequest } = testPatientsTestData;

    switch (paymentMethodOption) {
      case TestPatientPaymentMethodOption.NoPaymentMethods: {
        return this.resetTestPatientPaymentMethods(ctx, patientId);
      }
      case TestPatientPaymentMethodOption.OneCreditCard: {
        return concat(
          this.resetTestPatientPaymentMethods(ctx, patientId),
          defer(this.createTestPatientCreditCard.bind(this, ctx, patientId, createFirstCreditCardRequest))
        );
      }
      case TestPatientPaymentMethodOption.OneExpiredCreditCard: {
        return concat(
          this.resetTestPatientPaymentMethods(ctx, patientId),
          defer(this.createTestPatientCreditCard.bind(this, ctx, patientId, createFirstCreditCardRequest)),
          defer(this.expireTestPatientCreditCard.bind(this, ctx, patientId, 0))
        );
      }
      case TestPatientPaymentMethodOption.OneCreditCardAndOneBankAccount: {
        return concat(
          this.resetTestPatientPaymentMethods(ctx, patientId),
          defer(this.createTestPatientCreditCard.bind(this, ctx, patientId, createFirstCreditCardRequest)),
          defer(this.createTestPatientBankAccount.bind(this, ctx, patientId))
        );
      }
      case TestPatientPaymentMethodOption.OneValidAndOneExpiredCreditCard: {
        return concat(
          this.resetTestPatientPaymentMethods(ctx, patientId),
          defer(this.createTestPatientCreditCard.bind(this, ctx, patientId, createFirstCreditCardRequest)),
          defer(this.createTestPatientCreditCard.bind(this, ctx, patientId, createSecondCreditCardRequest)),
          defer(this.expireTestPatientCreditCard.bind(this, ctx, patientId, 1))
        );
      }
      default: {
        return of(null);
      }
    }
  }

  private clearTestPatientNotifications(ctx: StateContext<TestPatientsStateModel>, patientId: number) {
    ctx.setState(
      produce((draft) => {
        draft.clearTestPatientNotifications.isLoading = true;
      })
    );

    return this.apiService.clearTestPatientNotifications(patientId).pipe(
      tap({
        next: () => this.handleSuccess({ ctx, stateKey: 'clearTestPatientNotifications' }),
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'clearTestPatientNotifications' })
      )
    );
  }

  private createTestPatientNotification(
    ctx: StateContext<TestPatientsStateModel>,
    body: ApiCreateTestPatientNotificationRequest,
    patientId: number
  ) {
    if (ctx.getState().clearTestPatientNotifications.error) {
      return of(null);
    }

    ctx.setState(
      produce((draft) => {
        draft.createTestPatientNotification.isLoading = true;
      })
    );

    return this.apiService.createTestPatientNotification(body, patientId).pipe(
      tap({
        next: () => this.handleSuccess({ ctx, stateKey: 'createTestPatientNotification' }),
      }),
      catchError(
        (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'createTestPatientNotification' })
      )
    );
  }

  private handleNotification(
    ctx: StateContext<TestPatientsStateModel>,
    notificationOption: TestPatientNotificationOption,
    patientId: number
  ): Observable<any> {
    if (notificationOption === TestPatientNotificationOption.DoNothing) {
      return of(null);
    }

    if (notificationOption === TestPatientNotificationOption.NoNotifications) {
      return this.clearTestPatientNotifications(ctx, patientId);
    } else if (notificationOption !== TestPatientNotificationOption.TwoActionNotifications) {
      let body: ApiCreateTestPatientNotificationRequest | null = null;

      if (notificationOption === TestPatientNotificationOption.ActionNotificationWithOneButton) {
        body = testPatientsTestData.createOneButtonActionNotificationRequest(patientId);
      } else if (notificationOption === TestPatientNotificationOption.ActionNotificationWithTwoButtons) {
        body = testPatientsTestData.createTwoButtonActionNotificationRequest(patientId);
      } else if (notificationOption === TestPatientNotificationOption.DisplayNotification) {
        body = testPatientsTestData.createDisplayNotificationRequest(patientId);
      } else if (notificationOption === TestPatientNotificationOption.ExpirationNotification) {
        body = testPatientsTestData.createExpirationNotificationRequest(patientId);
      } else {
        body = testPatientsTestData.createCgm90EnrollmentNotificationRequest(
          patientId,
          this.envService.notifications.cgm90EnrollmentActionId
        );
      }

      return concat(
        this.clearTestPatientNotifications(ctx, patientId),
        defer(this.createTestPatientNotification.bind(this, ctx, body, patientId))
      );
    } else {
      const body1 = testPatientsTestData.createOneButtonActionNotificationRequest(patientId);
      const body2 = testPatientsTestData.createTwoButtonActionNotificationRequest(patientId, 10);

      return concat(
        this.clearTestPatientNotifications(ctx, patientId),
        defer(this.createTestPatientNotification.bind(this, ctx, body1, patientId)),
        defer(this.createTestPatientNotification.bind(this, ctx, body2, patientId))
      );
    }
  }

  private handleCopayExclusion(
    ctx: StateContext<TestPatientsStateModel>,
    copayExclusionOption: TestPatientCopayExclusionOption,
    patientId: number
  ): Observable<any> {
    if (copayExclusionOption === TestPatientCopayExclusionOption.DoNothing) {
      return of(null);
    }

    const body = testPatientsTestData.createTestPatientCopayExclusionRequest(
      copayExclusionOption === TestPatientCopayExclusionOption.AddToExclusionList
    );

    ctx.setState(
      produce((draft) => {
        draft.setTestPatientCopayExclusion.isLoading = true;
      })
    );

    return this.apiService.setTestPatientCopayExclusion(body, patientId).pipe(
      tap({
        next: () => this.handleSuccess({ ctx, stateKey: 'setTestPatientCopayExclusion' }),
      }),
      catchError((error: Error): Observable<any> => {
        if (copayExclusionOption === TestPatientCopayExclusionOption.RemoveFromExclusionList) {
          // Most of the time, test patients page users won't care if removal went through or not
          // TODO Refine erors in BE and be more selective about which errors to ignore in here
          return this.handleSuccess({ ctx, stateKey: 'setTestPatientCopayExclusion' });
        }

        return this.handleError({ ctx, error, stateKey: 'setTestPatientCopayExclusion', resetData: false });
      })
    );
  }

  private handleDeductibleOverrides(
    ctx: StateContext<TestPatientsStateModel>,
    deductibleOverrideOption: TestPatientDeductibleOverrideOption,
    patientId: number
  ): Observable<any> {
    if (deductibleOverrideOption === TestPatientDeductibleOverrideOption.DoNothing) {
      return of(null);
    }

    let allowPrimaryOverride: boolean;
    let allowSecondaryOverride: boolean;

    switch (deductibleOverrideOption) {
      case TestPatientDeductibleOverrideOption.PrimaryAllowedSecondaryAllowed:
        allowPrimaryOverride = true;
        allowSecondaryOverride = true;
        break;
      case TestPatientDeductibleOverrideOption.PrimaryAllowedSecondaryDisallowed:
        allowPrimaryOverride = true;
        allowSecondaryOverride = false;
        break;
      case TestPatientDeductibleOverrideOption.PrimaryDisallowedSecondaryAllowed:
        allowPrimaryOverride = false;
        allowSecondaryOverride = true;
        break;
      case TestPatientDeductibleOverrideOption.PrimaryDisallowedSecondaryDisallowed:
        allowPrimaryOverride = false;
        allowSecondaryOverride = false;
        break;
    }

    const body = testPatientsTestData.createTestPatientDeductibleOverridesRequest(
      allowPrimaryOverride,
      allowSecondaryOverride
    );

    ctx.setState(
      produce((draft) => {
        draft.setTestPatientDeductibleOverrides.isLoading = true;
      })
    );

    return this.apiService.setTestPatientDeductibleOverrides(body, patientId).pipe(
      tap({
        next: () => this.handleSuccess({ ctx, stateKey: 'setTestPatientDeductibleOverrides' }),
      }),
      catchError((error: Error): Observable<any> => {
        return this.handleError({ ctx, error, stateKey: 'setTestPatientDeductibleOverrides', resetData: false });
      })
    );
  }

  private cleanUpLoadTestPatient(ctx: StateContext<TestPatientsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.testPatient.isLoading = false;
      })
    );

    return of(null);
  }
}
